import { Component } from '@theme/component';

/**
 * @typedef {{
 *   timer: HTMLTimeElement,
 *   days: HTMLElement,
 *   hours: HTMLElement,
 *   minutes: HTMLElement,
 *   seconds: HTMLElement,
 *   status: HTMLElement
 * }} HeroCountdownRefs
 */

class HeroCountdownComponent extends Component {
  requiredRefs = ['timer', 'days', 'hours', 'minutes', 'seconds', 'status'];

  /** @type {number | undefined} */
  #timeout;

  #hasAnnouncedExpiration = false;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    super.disconnectedCallback();
  }

  #start() {
    this.#stop();
    this.#hasAnnouncedExpiration = false;

    const deadline = Date.parse(this.dataset.deadline ?? '');

    if (Number.isNaN(deadline)) {
      this.dataset.invalidDeadline = 'true';
      this.refs.timer.removeAttribute('datetime');
      return;
    }

    delete this.dataset.invalidDeadline;
    this.refs.timer.dateTime = new Date(deadline).toISOString();
    this.#update(deadline);
  }

  /** @param {number} deadline */
  #update(deadline) {
    const remainingSeconds = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
    const days = Math.floor(remainingSeconds / 86400);
    const hours = Math.floor((remainingSeconds % 86400) / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;

    this.refs.days.textContent = String(days).padStart(2, '0');
    this.refs.hours.textContent = String(hours).padStart(2, '0');
    this.refs.minutes.textContent = String(minutes).padStart(2, '0');
    this.refs.seconds.textContent = String(seconds).padStart(2, '0');

    if (remainingSeconds === 0) {
      if (!this.#hasAnnouncedExpiration) {
        this.refs.status.textContent = this.refs.status.dataset.expiredText ?? '';
        this.#hasAnnouncedExpiration = true;
      }
      return;
    }

    const delay = 1020 - (Date.now() % 1000);
    this.#timeout = window.setTimeout(() => this.#update(deadline), delay);
  }

  #stop() {
    if (this.#timeout !== undefined) {
      window.clearTimeout(this.#timeout);
      this.#timeout = undefined;
    }
  }
}

if (!customElements.get('hero-countdown-component')) {
  customElements.define('hero-countdown-component', HeroCountdownComponent);
}
